// src/pages/ItemAdjustment/itemAdjustmentSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAdjustmentVoucherNo,
  createItemAdjustment,
  fetchBatchesForItem,
  fetchStockForItem,
  fetchBatchSerials,
} from "./itemAdjustmentThunk";
import { currentDateTime } from "../../lib/functions";

// helper to create an empty item row
const emptyRow = (i = 1) => ({
  key: `a-${i}`,
  item_id: null,
  product: "",
  batch_id: null,
  available_qty: 0,
  current_qty: 0,
  loose_qty: 0,
  batch_controlled_qty: 0,
  has_batch_serial: false,
  quantity: 0, // ✅ Start with 0, not 1
  rate: 0,
  total: 0,
  is_serial: "no",
  serials: [],
  adjustment_type: "add_stock",
  currentStock: null,
  serial_mode: "unit",

});

const initialState = {
  // voucherNo: "",
  itemsList: [emptyRow()],
  footerValues: {
    total: 0,
    narration: "",
  },
  batches: {}, // batch options loaded when selecting item
  serials: {},
  stock: {},
  loading: false,
  success: null,
  entryDate: currentDateTime(),
  selectedItem: null,
  isTatmeenProduct: false,
};

const itemAdjustmentSlice = createSlice({
  name: "itemAdjustment",
  initialState,
  reducers: {
    setResetAdjustment(state) {
      state.itemsList = [emptyRow()];
      state.footerValues = { total: 0, narration: "" };
      state.batches = {};
      state.stock = {};
      state.serials = {};
      state.success = null;
      state.entryDate = new Date().toISOString();
      state.selectedItem = null;
      state.isTatmeenProduct = false;
      state.a_id = null; // ✅ Clear adjustment ID
      state.voucherNo = null; // ✅ Clear voucher number
    },
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },
    setItemsList(state, action) {
      state.itemsList = action.payload;
    },
    addAdjustmentRow(state) {
      state.itemsList.push(emptyRow(state.itemsList.length + 1));
    },
    deleteAdjustmentRow(state, action) {
      state.itemsList = state.itemsList.filter((r) => r.key !== action.payload);
      if (!state.itemsList.length) state.itemsList = [emptyRow()];
    },
    updateRow(state, action) {
      const { key, data } = action.payload;
      state.itemsList = state.itemsList.map((r) =>
        r.key === key ? { ...r, ...data } : r
      );
    },
    calculateTotals(state) {
      const total = state.itemsList.reduce(
        (s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0),
        0
      );
      state.footerValues = {
        ...state.footerValues,
        total: Number(total.toFixed(2)),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdjustmentVoucherNo.fulfilled, (state, action) => {
        state.voucherNo = action.payload;
      })
      .addCase(fetchStockForItem.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        state.stock[itemId] = data;
      })
      .addCase(fetchBatchesForItem.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        state.batches[itemId] = data;
      })
      .addCase(createItemAdjustment.fulfilled, (state, action) => {
        state.success = action.payload;
      })
      .addCase(fetchBatchSerials.fulfilled, (state, action) => {
        const { batchId, data } = action.payload;
        state.serials[batchId] = data;
      })
  },
});

export const {
  setResetAdjustment,
  setFormField,
  setItemsList,
  addAdjustmentRow,
  deleteAdjustmentRow,
  updateRow,
  calculateTotals,
} = itemAdjustmentSlice.actions;

export default itemAdjustmentSlice.reducer;
